import type { ReactNode } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { useTranslation } from 'react-i18next'

import type { Tag } from '@/api/tagsApi'
import type { EnumOption } from '@/api/enums'
import type { WordAttributeFilterKey } from '@/constants/wordAttributeFilterKeys'
import { TestCreateSection } from '../TestCreateSection'
import {
  ATTRIBUTE_META,
  OPERATORS_BY_TYPE,
  defaultOperator,
  newAttributeConditionRow,
} from '../wordAttributeFilters'
import type { AttributeConditionRow, AttributeOperator } from '../wordAttributeFilters'

/**
 * Отбор слов для теста: теги, условия по атрибутам и послабление фильтров.
 *
 * Блок «конкретные карточки» приходит слотом: он есть только у учителя и тянет
 * за собой поиск по его словарю, а секция должна оставаться общей для обеих
 * форм конструктора.
 */
export function TestWordFiltersSection({
  tags,
  selectedTagIds,
  onSelectedTagIdsChange,
  attributeConditions,
  onAttributeConditionsChange,
  attributeKeys,
  enumOptions,
  relaxFilters,
  onRelaxFiltersChange,
  explicitCardsSlot,
}: {
  tags: Tag[]
  selectedTagIds: number[]
  onSelectedTagIdsChange: (ids: number[]) => void
  attributeConditions: AttributeConditionRow[]
  onAttributeConditionsChange: (update: (prev: AttributeConditionRow[]) => AttributeConditionRow[]) => void
  /** Атрибуты, доступные для фильтра: зависят от загруженных справочников. */
  attributeKeys: WordAttributeFilterKey[]
  enumOptions: Record<string, EnumOption[]>
  relaxFilters: boolean
  onRelaxFiltersChange: (value: boolean) => void
  /** Только у учителя: выбор конкретных карточек ученика. */
  explicitCardsSlot?: ReactNode
}) {
  const { t } = useTranslation()

  return (
    <TestCreateSection
      id="word-filters"
      icon={<FilterAltIcon fontSize="small" />}
      title={t('tests.createForm.sectionWordFilters')}
      spacing={3}
    >
      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {t('tests.createForm.wordFilters.tags')}
        </Typography>
        <Autocomplete
          multiple
          options={tags}
          getOptionLabel={(o) => o.name}
          value={tags.filter((tg) => selectedTagIds.includes(tg.id))}
          onChange={(_, value) => onSelectedTagIdsChange(value.map((v) => v.id))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={t('tests.createForm.wordFilters.tagsPlaceholder')}
              variant="outlined"
            />
          )}
        />
      </Stack>

      <Stack spacing={1.25}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {t('tests.createForm.wordFilters.byAttributes')}
        </Typography>
        <Stack spacing={1}>
          {attributeConditions.map((row) => {
            const meta = ATTRIBUTE_META[row.field]
            const ops = OPERATORS_BY_TYPE[meta.inputType]
            const enumItems = meta.enumKey ? (enumOptions[meta.enumKey] ?? []) : []
            const needsValue = row.operator !== 'has' && row.operator !== 'has_not'

            return (
              <Stack key={row.id} direction="row" spacing={0.75} alignItems="center">
                <TextField
                  select
                  size="small"
                  label={t('tests.createForm.wordFilters.attribute')}
                  value={row.field}
                  onChange={(e) => {
                    const v = e.target.value as WordAttributeFilterKey
                    if (!attributeKeys.includes(v)) return
                    onAttributeConditionsChange((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, field: v, operator: defaultOperator(v), value: '' } : r,
                      ),
                    )
                  }}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
                  sx={{ minWidth: 150, flexShrink: 0 }}
                >
                  {attributeKeys.map((key) => (
                    <MenuItem key={key} value={key}>
                      {t(`tests.createForm.wordFilters.attributes.${key}`)}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label={t('tests.createForm.wordFilters.condition')}
                  value={row.operator}
                  onChange={(e) =>
                    onAttributeConditionsChange((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, operator: e.target.value as AttributeOperator, value: '' } : r,
                      ),
                    )
                  }
                  sx={{ minWidth: meta.inputType === 'text' ? 145 : 110, flexShrink: 0 }}
                >
                  {ops.map((op) => (
                    <MenuItem key={op} value={op}>
                      {t(`tests.createForm.wordFilters.operators.${op}`)}
                    </MenuItem>
                  ))}
                </TextField>

                {needsValue && meta.inputType === 'enum' ? (
                  <TextField
                    select
                    size="small"
                    label={t('tests.createForm.wordFilters.value')}
                    value={row.value}
                    onChange={(e) =>
                      onAttributeConditionsChange((prev) =>
                        prev.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)),
                      )
                    }
                    sx={{ flex: 1, minWidth: 0 }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}
                  >
                    {enumItems.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : needsValue && meta.inputType === 'text' ? (
                  <TextField
                    size="small"
                    label={t('tests.createForm.wordFilters.value')}
                    value={row.value}
                    onChange={(e) =>
                      onAttributeConditionsChange((prev) =>
                        prev.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)),
                      )
                    }
                    placeholder={t('tests.createForm.wordFilters.valuePlaceholder')}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                ) : (
                  /* boolean — поле не нужно, растягиваем пространство */
                  <Box sx={{ flex: 1 }} />
                )}

                <IconButton
                  aria-label={t('tests.createForm.wordFilters.removeCondition')}
                  size="small"
                  onClick={() => onAttributeConditionsChange((prev) => prev.filter((r) => r.id !== row.id))}
                  sx={{ flexShrink: 0 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            )
          })}
          <Button
            type="button"
            size="small"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => onAttributeConditionsChange((prev) => [...prev, newAttributeConditionRow()])}
            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
          >
            {t('tests.createForm.wordFilters.addCondition')}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          {t('tests.createForm.wordFilters.conditionsHint')}
        </Typography>
      </Stack>

      {explicitCardsSlot}

      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {t('tests.createForm.wordFilters.relax')}
        </Typography>
        <Switch
          checked={relaxFilters}
          onChange={(e) => onRelaxFiltersChange(e.target.checked)}
          color="primary"
          sx={{ flexShrink: 0 }}
        />
      </Stack>
    </TestCreateSection>
  )
}
